import { action, computed, makeObservable, observable } from 'mobx'

import { LogisticsFilterType } from '~/client/src/shared/enums/LogisticsFilterType'
import LogisticsGroupingOption from '~/client/src/shared/enums/LogisticsGroupingOption'
import ILogisticItem from '~/client/src/shared/models/ILogisticItem'
import SitePermit from '~/client/src/shared/models/Permit'
import BaseLogisticsList from '~/client/src/shared/stores/BaseLogisticsLists.store'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import SharedSitePermitStore from '~/client/src/shared/stores/domain/SharedSitePermit.store'
import { IFilters } from '~/client/src/shared/stores/InitialState'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import {
  createFilterValueByType,
  getSelectedFilterValues,
  extractWorkflowPermits,
} from '~/client/src/shared/utils/logisticsHelper'
import { UNASSIGNED } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import MobileInitialState from '../../../stores/MobileInitialState'
import MobileCommonStore from '../../../stores/ui/MobileCommon.store'
import MobileLogisticsStore from './MobileLogistics.store'

export default class MobileLogisticsListStore implements BaseLogisticsList {
  @observable public isFiltersModalOpen: boolean = false
  @observable public isGroupByFilterShown: boolean = false

  public constructor(
    private readonly logisticsStore: MobileLogisticsStore,
    private readonly projectDateStore: ProjectDateStore,
    private readonly projectMembersStore: ProjectMembersStore,
    private readonly companiesStore: CompaniesStore,
    private readonly common: MobileCommonStore,
    private readonly state: MobileInitialState,
    private readonly sharedSitePermitStore: SharedSitePermitStore,
    private openPermit: (permit: SitePermit) => void,
  ) {
    makeObservable(this)
  }

  private get filter(): IFilters {
    return this.logisticsStore.filters
  }

  @computed
  public get groupingKey() {
    return this.filter.groupingKey
  }

  @action.bound
  public toggleFilters() {
    this.isFiltersModalOpen = !this.isFiltersModalOpen
  }

  @action.bound
  public toggleGroupBy() {
    this.isGroupByFilterShown = !this.isGroupByFilterShown
  }

  @action.bound
  public selectGroupBy(option: LogisticsGroupingOption) {
    this.filter.groupingKey = option
  }

  private get filterValueByType() {
    return createFilterValueByType(
      this.logisticsStore.getSharedFormById,
      () => this.state.activeProject.id,
    )
  }

  public getFilteredCollectionExcludeFilter = (
    excludedFilters: Array<LogisticsFilterType | string> = [],
  ) => {
    let filteredCollection = this.isSearchFilterActive
      ? this.instancesInPeriodInterval.filter(this.searchFiltering)
      : this.instancesInPeriodInterval

    this.activeFilterTypes.forEach(filterType => {
      const shouldUseFilter = !excludedFilters.includes(filterType)

      if (!shouldUseFilter) {
        return
      }

      const filter = this.filter.fieldsMap[filterType]
      const { initialFilterOptions } = filter
      const selectedValues = getSelectedFilterValues(
        filterType,
        initialFilterOptions,
      )

      filteredCollection = filteredCollection.filter(inst => {
        const valueGetter = this.filterValueByType[filterType]
        const value = valueGetter(inst)

        if (initialFilterOptions.has(UNASSIGNED) && !value.length) {
          return true
        }

        return value.some(v => selectedValues.has(v))
      })
    })

    return filteredCollection
  }

  @action.bound
  public onPermitClick(permit: SitePermit) {
    const sharedPermit = this.sharedSitePermitStore.getById(permit.id)
    if (sharedPermit) {
      this.common.displaySharedFormView(sharedPermit.id)
    } else {
      this.openPermit(permit)
    }
  }

  @computed
  public get filteredCollection(): ILogisticItem[] {
    return this.getFilteredCollectionExcludeFilter()
  }

  @computed
  public get workflowPermits(): SitePermit[] {
    return extractWorkflowPermits(this.filteredCollection)
  }

  private searchFiltering = (logistic: ILogisticItem) => {
    const key = this.filter.searchKey.toLowerCase()
    const entityValues: string[] = [
      ...logistic.locations.map(l => l.name),
      logistic.name,
      this.projectDateStore.getWeekdayMonthDayAndTimeToDisplay(
        logistic.startDate,
      ),
      this.projectDateStore.getWeekdayMonthDayAndTimeToDisplay(
        logistic.endDate,
      ),
      logistic.app,
      logistic.status,
      ...this.projectMembersStore
        .getByIds(logistic.responsibleContactIds)
        .map(u => u.fullName),
      ...logistic.companyIds.map(id =>
        this.companiesStore.getCompanyNameById(id),
      ),
    ]
    return entityValues.some(value => value?.toLowerCase().includes(key))
  }

  private get isSearchFilterActive(): boolean {
    return !!this.filter.searchKey
  }

  private get instancesInPeriodInterval() {
    return this.logisticsStore.logisticItemsInPeriodInterval
  }

  @computed
  public get activeFilterTypes(): string[] {
    const { fieldsMap = {} } = this.filter
    return Object.keys(fieldsMap).filter(
      filterType => fieldsMap[filterType].isFilterActive,
    )
  }
}
